import {
  cloneElement,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import styles from "@/styles/multiSelectSearch.module.css";

// PrimeReact's MultiSelect doesn't export the option types these templates
// receive, and the ones it exposes via its own .d.ts (MultiSelectTemplateOptions,
// MultiSelectPanelHeaderTemplateEvent) aren't public exports either — these
// narrow local types cover only the fields actually used below.
type FilterTemplateOptions = {
  // Actually a JSX element at runtime — PrimeReact's own .d.ts mistypes this
  // as HTMLDivElement, so this is widened just enough to accept what it
  // really passes without losing ReactNode as our own return type below.
  element: ReactNode | HTMLDivElement;
  filterOptions?: { reset?: () => void };
};

export type PanelHeaderTemplateOptions = {
  className: string;
  checkboxElement: ReactNode;
  filterElement: ReactNode;
  closeElement: ReactElement<{ onClick?: (event: ReactMouseEvent) => void }>;
  onCloseClick: (event: ReactMouseEvent<HTMLElement>) => void;
};

// The panel's built-in close (X) button always closes the panel, even when
// the user meant to clear a typed search — this hook makes it context-aware:
// clear the search first if there's a query, only close once it's empty.
// `filterTemplate` is the one officially supported hook PrimeReact gives us
// for reaching the internal filter-reset callback, so it's used here purely
// to capture that callback — the search box it renders is left untouched.
// `headerClassName` is added to the shared search-header class, for panels
// that need to adjust it (e.g. the campaign picker's divider).
const useFilterReset = (headerClassName?: string) => {
  const [hasFilterValue, setHasFilterValue] = useState(false);
  const resetRef = useRef<(() => void) | undefined>(undefined);

  const filterTemplate = ({
    element,
    filterOptions,
  }: FilterTemplateOptions): ReactNode => {
    resetRef.current = filterOptions?.reset;
    return element as ReactNode;
  };

  const onFilter = ({ filter }: { filter: string }) => {
    setHasFilterValue(Boolean(filter));
  };

  const buildHeader = ({
    className,
    checkboxElement,
    filterElement,
    closeElement,
    onCloseClick,
  }: PanelHeaderTemplateOptions) => (
    <div
      className={[className, styles.header, headerClassName]
        .filter(Boolean)
        .join(" ")}
    >
      {checkboxElement}
      {filterElement}
      {cloneElement(closeElement, {
        onClick: (event: ReactMouseEvent) =>
          hasFilterValue
            ? resetRef.current?.()
            : onCloseClick(event as ReactMouseEvent<HTMLElement>),
      })}
    </div>
  );

  return { filterTemplate, onFilter, buildHeader };
};

export default useFilterReset;
